// /client/src/components/Products.js
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from './Navigation';

const Products = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State management
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('active');
  
  // State for sorting
  const [sortConfig, setSortConfig] = useState({
    key: 'createdAt',
    direction: 'descending'
  });

  // Fetch products from API
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No authentication token found');
        return;
      }
      
      const response = await fetch('/api/products', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data.data || []);
      } else {
        const errorData = await response.json();
        console.error('API error:', errorData);
        setError(errorData.error || 'Failed to fetch products');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  // Check for success message from location state
  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Sorting functions
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Filter and sort products
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...products];
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(product => {
        const name = product.name?.toLowerCase() || '';
        return name.includes(searchLower);
      });
    }
    
    // Apply status filter
    if (selectedStatus) {
      filtered = filtered.filter(product => product.status === selectedStatus);
    }
    
    // Apply sorting
    if (sortConfig !== null) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        if (sortConfig.key === 'createdAt') {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        } else if (sortConfig.key === 'days') {
          aValue = parseInt(aValue || 0);
          bValue = parseInt(bValue || 0);
        } else {
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filtered;
  }, [products, searchTerm, selectedStatus, sortConfig]);

  const getSortIcon = (columnName) => {
    const iconStyle = {
      width: '12px',
      height: '12px',
      marginLeft: '8px',
      display: 'inline-block'
    };

    if (sortConfig.key !== columnName) {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 5.83L15.17 9H13v6h-2V9H8.83L12 5.83zM12 18.17L8.83 15H11V9h2v6h2.17L12 18.17z"/>
        </svg>
      );
    }
    
    if (sortConfig.direction === 'ascending') {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 6.83L15.17 10H13v8h-2v-8H8.83L12 6.83z"/>
        </svg>
      );
    } else {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 17.17L8.83 14H11V6h2v8h2.17L12 17.17z"/>
        </svg>
      );
    }
  };

  const handleRowClick = (productId) => {
    navigate(`/products/edit/${productId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleCreateProduct = () => {
    navigate('/products/create');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'is-success';
      case 'in delivery':
        return 'is-info';
      case 'archived':
        return 'is-warning';
      default:
        return 'is-light';
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedStatus('');
  };

  if (loading) {
    return (
      <>
        <section className="theme-light m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column is-justify-content-center'>
            <Navigation />
            <div className='mt-6 pt-6 container'>
              <div className="has-text-centered py-6">
                <div className="is-loading"></div>
                <p>Loading products...</p>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          
          <Navigation />

          <div className='mt-2 pt-6 container px-6'>
            <div className='pb-4 is-flex is-justify-content-space-between is-align-items-center'>
              <h1 className='h1 my-2'>Products</h1>
              <div className="buttons">
                <button 
                  className="button"
                  onClick={handleCreateProduct}
                >
                  Create New Product
                </button>
              </div>
            </div>

            {successMessage && (
              <div className="notification is-success">
                <button 
                  className="delete" 
                  onClick={() => setSuccessMessage('')}
                ></button>
                {successMessage}
              </div>
            )}

            {error && (
              <div className="notification is-danger">
                <button 
                  className="delete" 
                  onClick={() => setError('')}
                ></button>
                {error}
                <br />
                <button 
                  className="button is-small is-light mt-2" 
                  onClick={fetchProducts}
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Search and Filter Section */}
            <div className="box mb-4">
              <div className="columns">
                <div className="column is-7">
                  <div className="field">
                    <label className="pt-0 label">Search</label>
                    <div className="control has-icons-left">
                      <input
                        className="input"
                        type="text"
                        placeholder="Search by product name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <span className="icon is-small is-left">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <circle cx="11" cy="11" r="8"/>
                          <path d="m21 21-4.35-4.35"/>
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="column is-4">
                  <div className="field">
                    <label className="pt-0 label">Filter by Status</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select
                          value={selectedStatus}
                          onChange={(e) => setSelectedStatus(e.target.value)}
                        >
                          <option value="">All Statuses</option>
                          <option value="active">Active</option>
                          <option value="in delivery">In Delivery</option>
                          <option value="archived">Archived</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="column is-1">
                  <div className="field">
                    <label className="pt-0 label">&nbsp;</label>
                    <div className="control">
                      <button 
                        className="button is-light is-fullwidth"
                        onClick={clearFilters}
                        disabled={!searchTerm && !selectedStatus}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {filteredAndSortedProducts.length === 0 && !loading && !error ? (
              <div className="notification is-info is-light">
                <p>
                  {searchTerm || selectedStatus
                    ? 'No products match your search criteria.'
                    : 'No products found. Create your first product to get started.'}
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4 is-flex is-justify-content-space-between is-align-items-center">
                  <p className="is-size-7 has-text-grey">
                    Showing {filteredAndSortedProducts.length} of {products.length} product{products.length !== 1 ? 's' : ''}
                  </p>
                  {(searchTerm || selectedStatus) && (
                    <div className="tags">
                      {searchTerm && (
                        <span className="tag is-info">
                          Search: "{searchTerm}"
                          <button 
                            className="delete is-small" 
                            onClick={() => setSearchTerm('')}
                          ></button>
                        </span>
                      )}
                      {selectedStatus && (
                        <span className="tag is-info">
                          Status: {selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1)}
                          <button 
                            className="delete is-small" 
                            onClick={() => setSelectedStatus('')}
                          ></button>
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="table-container">
                  <table className="table is-fullwidth is-hoverable">
                    <thead>
                      <tr>
                        <th 
                          onClick={() => handleSort('name')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'name' ? 'colorBlack' : 'colorBlue'}
                        >
                          Product Name {getSortIcon('name')}
                        </th>
                        <th 
                          onClick={() => handleSort('days')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'days' ? 'colorBlack' : 'colorBlue'}
                        >
                          Messages {getSortIcon('days')}
                        </th>
                        <th 
                          onClick={() => handleSort('status')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'status' ? 'colorBlack' : 'colorBlue'}
                        >
                          Status {getSortIcon('status')}
                        </th>
                        <th 
                          onClick={() => handleSort('createdAt')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'createdAt' ? 'colorBlack' : 'colorBlue'}
                        >
                          Created {getSortIcon('createdAt')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAndSortedProducts.map((product) => {
                        const productId = product.id || product._id;
                        
                        return (
                          <tr 
                            key={productId}
                            onClick={() => handleRowClick(productId)}
                            style={{
                              cursor: 'pointer',
                              transition: 'background-color 0.2s ease, transform 0.1s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f0f0f0';
                              e.currentTarget.style.transform = 'scale(1.01)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            <td>
                              {product.name}
                            </td>
                            <td>{product.days}</td>
                            <td>
                              <span className={`tag ${getStatusColor(product.status)}`}>
                                {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
                              </span>
                            </td>
                            <td>{formatDate(product.createdAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

          </div>

        </div>
      </section>
    </>
  );
};

export default Products;